import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import ImagePreviewModal from './ImagePreviewModal';
import PdfThumbnail from './PdfThumbnail';
import { ShoppingCart, Plus, ChevronDown, Printer, FileDown } from 'lucide-react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { loadsAPI, uploadsAPI, firingSessionsAPI, firearmsAPI, purchasesAPI, usersAPI, conversationsAPI, recipesAPI } from '../services/api';
import { formatLocalDate } from '../services/date';

const RecipeModal = ({
  show,
  recipe,
  onClose,
  onEdit,
  onImagesUpdated,
  onLoadsUpdated,
  velocityStatsMap,
  // Pre-fetched data props to avoid redundant API calls
  initialLoads,
  initialFirearms,
  initialSessions,
  initialImages,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [images, setImages] = useState([]);
  const [loads, setLoads] = useState([]);
  const [firearms, setFirearms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [, setLoading] = useState(false);
  const [componentAvailability, setComponentAvailability] = useState([]);
  const [recipeStats, setRecipeStats] = useState({});
  const [recipeDetail, setRecipeDetail] = useState(recipe || {});
  const [modalVelocityStats, setModalVelocityStats] = useState([]);
  const [hasInitializedFromProps, setHasInitializedFromProps] = useState(false);

  const [showAddLoads, setShowAddLoads] = useState(false);
  const [showSession, setShowSession] = useState(false);
  const [showSessionList, setShowSessionList] = useState(false);
  const [caseBatches, setCaseBatches] = useState([]);
  const [showShare, setShowShare] = useState(false);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseComponent, setPurchaseComponent] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '' });
  const [filePreview, setFilePreview] = useState(null);
  const sessionImportInputRef = useRef(null);
  const [addLoadFiles, setAddLoadFiles] = useState([]);
  const [sessionFiles, setSessionFiles] = useState([]);
  const [sessionPreviews, setSessionPreviews] = useState([]);
  const [importingSessionVelocities, setImportingSessionVelocities] = useState(false);

  const { register: registerAddLoad, handleSubmit: handleSubmitAddLoad, reset: resetAddLoad, formState: { errors: errorsAddLoad } } = useForm();
  const { register: registerSession, handleSubmit: handleSubmitSession, reset: resetSession, control: controlSession, formState: { errors: errorsSession }, getValues: getSessionValues } = useForm({ defaultValues: { velocities: [] } });
  const { fields: fieldsSession, append: appendSessionVelocity, remove: removeSessionVelocity, replace: replaceSessionVelocities } = useFieldArray({ control: controlSession, name: 'velocities' });
  const sessionVelocityValues = useWatch({ control: controlSession, name: 'velocities' }) || [];
  const hasSessionVelocities = sessionVelocityValues.length > 0;
  const enteredSessionVelocityCount = sessionVelocityValues.reduce((count, entry) => {
    const num = Number(entry?.velocity);
    return Number.isFinite(num) ? count + 1 : count;
  }, 0);

  const applyImportedSessionVelocities = (values) => {
    if (!Array.isArray(values) || values.length === 0) {
      return 0;
    }
    const sanitized = values
      .map((value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return null;
        return { velocity: `${Math.round(num)}` };
      })
      .filter(Boolean);
    if (sanitized.length === 0) {
      return 0;
    }
    const current = getSessionValues('velocities') || [];
    replaceSessionVelocities([...current, ...sanitized]);
    return sanitized.length;
  };

  const handleSessionVelocitiesImport = async (files) => {
    const importFiles = Array.from(files || []).filter(Boolean);
    if (importFiles.length === 0) return;
    setImportingSessionVelocities(true);
    let totalImported = 0;
    let hadError = false;
    for (const file of importFiles) {
      try {
        const res = await firingSessionsAPI.importVelocities(file);
        const parsed = Array.isArray(res?.data?.velocities) ? res.data.velocities : [];
        totalImported += applyImportedSessionVelocities(parsed);
      } catch (error) {
        hadError = true;
        console.error('Failed to import velocities', error);
        const message = error?.response?.data?.error || `Failed to import ${file.name}`;
        toast.error(message);
      }
    }
    // Show success message if any velocities were imported (even if some files failed)
    if (totalImported > 0) {
      toast.success(`Imported ${totalImported} velocities`);
    } else if (!hadError) {
      // Only show "no velocities found" if there were no errors (all files processed but none had data)
      toast.error('No velocities were found under SPEED (FPS).');
    }
    setImportingSessionVelocities(false);
  };

  const handleSessionVelocityFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    await handleSessionVelocitiesImport(files);
    setSessionFiles((prev) => [...prev, ...files]);
  };

  const handleClearSessionVelocities = () => {
    replaceSessionVelocities([]);
    toast.success('Velocities cleared');
  };

  const reload = useCallback(async (forceRefresh = false) => {
    if (!recipe) return;
    try {
      setLoading(true);
      
      // Use props if available and not forcing refresh, otherwise fetch
      const usePropsData = !forceRefresh && !hasInitializedFromProps;
      
      let loadsList, firearmsList, sessionsList;
      
      if (usePropsData && Array.isArray(initialLoads) && initialLoads.length >= 0) {
        loadsList = initialLoads;
        firearmsList = Array.isArray(initialFirearms) ? initialFirearms : [];
        sessionsList = Array.isArray(initialSessions) ? initialSessions : [];
        setLoads(loadsList);
        setFirearms(firearmsList);
        setSessions(sessionsList);
        setRecipeDetail(recipe);
        setHasInitializedFromProps(true);
      } else {
        // Fallback: fetch data (for modals opened from other pages or after actions)
        const [loadsRes, firearmsRes, sessionsRes, recipesRes] = await Promise.all([
          loadsAPI.getAll(),
          firearmsAPI.getAll(),
          firingSessionsAPI.getAll(),
          recipesAPI.getAll()
        ]);
        loadsList = Array.isArray(loadsRes.data) ? loadsRes.data : [];
        firearmsList = Array.isArray(firearmsRes.data) ? firearmsRes.data : [];
        sessionsList = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
        setLoads(loadsList);
        setFirearms(firearmsList);
        setSessions(sessionsList);
        try {
          const list = Array.isArray(recipesRes.data) ? recipesRes.data : [];
          const match = list.find(r => String(r.id) === String(recipe.id));
          if (match) setRecipeDetail(match);
        } catch {}
      }
      
      // Handle images: use props if available, otherwise fetch with batch API
      if (usePropsData && Array.isArray(initialImages)) {
        setImages(initialImages);
      } else {
        try {
          // Map ALL batch_ids to recipeId (loads are grouped, sessions reference specific IDs)
          const loadIdToRecipeId = new Map();
          loadsList.forEach(l => {
            const rid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
            const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
            ids.forEach(id => loadIdToRecipeId.set(id, rid));
          });
          const loadIdSet = new Set();
          loadsList.forEach(l => {
            const lrid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
            if (String(lrid) === String(recipe.id)) {
              const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
              ids.forEach(id => loadIdSet.add(id));
            }
          });
          const loadIds = Array.from(loadIdSet);
          const sessionIds = sessionsList.filter(s => {
            const loadId = (s && s.load && s.load.id) || s?.load_id;
            const rid = loadIdToRecipeId.get(loadId);
            return String(rid) === String(recipe.id);
          }).map(s => s.id);
          
          // Use batch API for images
          const queries = [
            { entity_type: 'recipe', entity_id: recipe.id },
            ...loadIds.map(id => ({ entity_type: 'load', entity_id: id })),
            ...sessionIds.map(id => ({ entity_type: 'firing_session', entity_id: id })),
          ];
          const batchRes = await uploadsAPI.batchList(queries);
          const batchData = batchRes?.data || {};
          
          const merged = [];
          const seen = new Set();
          Object.values(batchData).forEach(arr => {
            (arr || []).forEach(img => {
              if (img && !seen.has(img.id)) {
                seen.add(img.id);
                merged.push(img);
              }
            });
          });
          setImages(merged);
        } catch { setImages([]); }
      }
      
      // Always fetch fresh availability + stats (these are recipe-specific and change with purchases)
      try {
        const { data } = await api.get(`/recipes/${recipe.id}/cost`);
        setComponentAvailability(Array.isArray(data?.component_breakdown) ? data.component_breakdown : []);
        setRecipeStats({
          currentCostPerRound: data?.total_cost_per_round ?? null,
          totalSpent: data?.total_spent ?? 0,
          componentBreakdown: data?.component_breakdown ?? []
        });
      } catch {
        setComponentAvailability([]);
        setRecipeStats({});
      }
    } finally {
      setLoading(false);
    }
  }, [recipe, initialLoads, initialFirearms, initialSessions, initialImages, hasInitializedFromProps]);

  // Reset initialization flag when modal closes or recipe changes
  useEffect(() => {
    if (!show) {
      setHasInitializedFromProps(false);
    }
  }, [show]);

  useEffect(() => { if (show && recipe) { setRecipeDetail(recipe); reload(); } }, [show, recipe, reload]);

  useEffect(() => {
    if (!recipe) {
      setModalVelocityStats([]);
      return;
    }
    const key = String(recipe.id);
    const provided = velocityStatsMap && velocityStatsMap[key];
    if (provided) {
      setModalVelocityStats(provided);
      return;
    }
    let cancelled = false;
    recipesAPI.getVelocityStats([recipe.id])
      .then(res => {
        if (cancelled) return;
        const data = res?.data || {};
        setModalVelocityStats(data[key] || []);
      })
      .catch(() => {
        if (!cancelled) setModalVelocityStats([]);
      });
    return () => {
      cancelled = true;
    };
  }, [recipe, velocityStatsMap]);

  const getCompatibleFirearmsForRecipe = (r) => {
    const cart = (r?.cartridge || '').trim().toLowerCase();
    return firearms.filter(f => (f.caliber || '').trim().toLowerCase() === cart);
  };

  const getRecipeLoads = useMemo(() => (loads || []).filter(l => (l.recipe && l.recipe.id) === (recipe?.id)), [loads, recipe?.id]);
  const recipeSessions = useMemo(() => {
    try {
      // Build set of ALL load IDs (including all batch_ids) for this recipe
      const loadIds = new Set();
      getRecipeLoads.forEach(l => {
        const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
        ids.forEach(id => loadIds.add(id));
      });
      return (sessions || [])
        .filter(s => loadIds.has(s.load?.id))
        .sort((a, b) => new Date(b.session_date || 0) - new Date(a.session_date || 0));
    } catch {
      return [];
    }
  }, [sessions, getRecipeLoads]);

  const watchedSessionLoadId = useWatch({ control: controlSession, name: 'load_id' });
  const selectedSessionLoad = useMemo(
    () => getRecipeLoads.find((l) => String(l.id) === String(watchedSessionLoadId)),
    [getRecipeLoads, watchedSessionLoadId]
  );
  const minSessionRounds = Math.max(1, enteredSessionVelocityCount);
  const sessionMaxRounds = selectedSessionLoad && Number.isFinite(Number(selectedSessionLoad.remaining_quantity))
    ? Number(selectedSessionLoad.remaining_quantity)
    : null;
  const sessionHasMax = Number.isFinite(sessionMaxRounds);
  const sessionRoundsPlaceholder = sessionHasMax
    ? `Min ${minSessionRounds} • Max ${sessionMaxRounds}`
    : `Min ${minSessionRounds}`;

  const typeColorClass = (typeName) => {
    const t = (typeName || '').toLowerCase();
    if (t === 'bullet') return 'text-blue-600';
    if (t === 'primer') return 'text-red-600';
    if (t === 'powder') return 'text-orange-600';
    if (t === 'brass') return 'text-yellow-600';
    return 'text-gray-500';
  };

  // Build previews for files selected in the session modal
  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      try {
        const files = Array.from(sessionFiles || []);
        const results = await Promise.all(files.map(async (f) => {
          const name = f.name || '';
          const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
          const isPdf = /\.pdf$/i.test(name);
          const isDocLike = /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv|json|md|log)$/i.test(name);
          if (isImage || isPdf) {
            const url = URL.createObjectURL(f);
            return { url, filename: name, file: f, docThumb: false, revoke: true };
          }
          if (isDocLike) {
            try {
              const res = await uploadsAPI.thumbnailPreview(f);
              const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: 'image/png' });
              const url = URL.createObjectURL(blob);
              return { url, filename: name, file: f, docThumb: true, revoke: true };
            } catch {
              const url = URL.createObjectURL(f);
              return { url, filename: name, file: f, docThumb: false, revoke: true };
            }
          }
          const url = URL.createObjectURL(f);
          return { url, filename: name, file: f, docThumb: false, revoke: true };
        }));
        if (!cancelled) setSessionPreviews(results);
      } catch {
        if (!cancelled) setSessionPreviews([]);
      }
    };
    build();
    return () => {
      cancelled = true;
      try {
        (sessionPreviews || []).forEach(p => { if (p && p.revoke && p.url) URL.revokeObjectURL(p.url); });
      } catch {}
    };
  }, [sessionFiles]);

  const ensureRecipeQueryParam = useCallback(() => {
    if (!recipe?.id) return;
    if (location.pathname !== '/recipes') return;
    const params = new URLSearchParams(location.search || '');
    if (params.get('recipeId') === String(recipe.id)) return;
    params.set('recipeId', recipe.id);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.pathname, location.search, recipe?.id, navigate]);

  const handleSessionDeepLink = (sessionId) => {
    ensureRecipeQueryParam();
    if (typeof onClose === 'function') {
      onClose();
    }
    navigate(`/firing-sessions?sessionId=${sessionId}`);
  };

  const buildPrintHtml = (rec, targetUrl, qrSrc) => {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${rec.name || 'Recipe'}</title>
<style>body{font-family:system-ui,sans-serif;text-align:center;padding:40px 20px;}
h1{margin-bottom:8px;}p{margin:4px 0;color:#555;}.qr{margin:20px auto;}</style>
</head><body>
<h1>${rec.name || 'Recipe'}</h1>
<p>${rec.cartridge || rec.cartridge_other || ''}</p>
<div class="qr"><img id="qr" src="${qrSrc}" alt="QR" width="256" height="256" /></div>
<p style="font-size:12px;word-break:break-all;">${targetUrl}</p>
</body>
<script>
  (function(){
    function go(){ setTimeout(function(){ try{ window.focus(); window.print(); }catch(e){} }, 100); }
    var img = document.getElementById('qr');
    if (!img) { go(); return; }
    if (img.complete) { go(); } else { img.onload = go; img.onerror = go; }
  })();
</script>
</html>`;
  };

  const handlePrintRecipe = () => {
    if (!recipe) return;
    try {
      const origin = window.location.origin;
      const targetUrl = `${origin}/recipes?recipeId=${encodeURIComponent(recipe.id)}`;
      const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(targetUrl)}&margin=1`;
      const html = buildPrintHtml(recipe, targetUrl, qrSrc);
      const ua = (navigator?.userAgent || '').toLowerCase();
      const isMobile = /android|iphone|ipad|ipod/.test(ua);
      if (isMobile) {
        const w = window.open('', 'recipe-print', 'width=800,height=600');
        if (!w) {
          throw new Error('popup blocked');
        }
        w.document.write(html);
        w.document.close();
        return;
      }
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;height:600px;border:none;';
      document.body.appendChild(iframe);
      const idoc = iframe.contentDocument || iframe.contentWindow?.document;
      const iw = iframe.contentWindow;
      idoc.open();
      idoc.write(html);
      idoc.close();
      const remove = () => { try { document.body.removeChild(iframe); } catch {} };
      if (iw) {
        iw.onafterprint = remove;
        setTimeout(remove, 5000);
      } else {
        setTimeout(remove, 3000);
      }
    } catch {
      try {
        const origin2 = window.location.origin;
        const targetUrl2 = `${origin2}/recipes?recipeId=${encodeURIComponent(recipe.id)}`;
        const qrSrc2 = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(targetUrl2)}&margin=1`;
        const html2 = buildPrintHtml(recipe, targetUrl2, qrSrc2);
        const w = window.open('', 'recipe-print', 'width=800,height=600');
        if (!w) return;
        w.document.write(html2);
        w.document.close();
      } catch {}
    }
  };

  const getSessionCaseLabel = (session) => {
    const firingCount = session.load?.case_batch?.firing_count;
    if (typeof firingCount === 'number') return `${firingCount}x brass`;
    const fallback = session.load?.firing_count;
    if (typeof fallback === 'number') return `${fallback}x brass`;
    return 'New brass';
  };

  const findRecipeComponentForAvailability = (avItem) => {
    try {
      const compStr = (avItem.component || '').toLowerCase();
      return (recipe.components || []).find(rc => {
        const brand = (rc.component.brand || '').toLowerCase();
        const model = (rc.component.model || '').toLowerCase();
        return compStr.includes(brand) && compStr.includes(model);
      });
    } catch { return null; }
  };

  const maxLoadsFor = (avItem) => {
    const rc = findRecipeComponentForAvailability(avItem);
    if (!rc) return 0;
    const perLoad = Number(rc.quantity || 0);
    if (perLoad <= 0) return 0;
    const availableQty = Number(avItem.available_quantity || 0);
    return Math.floor(availableQty / perLoad);
  };

  const getTotalLoadsCreated = () => getRecipeLoads.reduce((sum, l) => sum + (Number(l.quantity_created) || 0), 0);
  const getRemainingLoads = () => getRecipeLoads.reduce((sum, l) => sum + (Number(l.remaining_quantity) || 0), 0);
  const getTotalSpent = () => getRecipeLoads.reduce((sum, l) => sum + (Number(l.total_cost) || 0), 0);
  const getAverageCostPerLoad = () => {
    const totalCost = getTotalSpent();
    const totalQty = getTotalLoadsCreated();
    return totalQty > 0 ? totalCost / totalQty : null;
  };

  // Velocity by Firearm (mirror card logic using modal-fetched sessions and loads)
  const velocityByFirearm = modalVelocityStats;

  const openAddLoads = async () => {
    try {
      const res = await loadsAPI.getCaseBatches(recipe.id);
      setCaseBatches(res.data || []);
    } catch { setCaseBatches([]); }
    setShowAddLoads(true);
  };

  const submitAddLoads = async (data) => {
    try {
      const res = await loadsAPI.create({
        recipe_id: recipe.id,
        case_batch_id: data.case_batch_id,
        quantity_created: Number(data.quantity_created || 0),
        date_created: data.date_created,
        notes: data.notes || ''
      });
      // If user selected files using Upload Files, upload them to the new load id and propagate updates
      try {
        const newId = res?.data?.id;
        if (newId && Array.isArray(addLoadFiles) && addLoadFiles.length > 0) {
          await uploadsAPI.upload('load', newId, Array.from(addLoadFiles));
          // broadcast to other tabs/pages
          try { window.dispatchEvent(new CustomEvent('uploadsChanged', { detail: { entity: 'load', id: newId, recipeId: recipe.id } })); } catch {}
          // update parent recipe thumbnails
          try {
            const [recImgs, loadImgs] = await Promise.all([
              uploadsAPI.list('recipe', recipe.id).catch(() => ({ data: [] })),
              uploadsAPI.list('load', newId).catch(() => ({ data: [] })),
            ]);
            const combined = [...(Array.isArray(recImgs?.data) ? recImgs.data : []), ...(Array.isArray(loadImgs?.data) ? loadImgs.data : [])];
            try { if (typeof onImagesUpdated === 'function') onImagesUpdated(recipe.id, combined); } catch {}
          } catch {}
        }
      } catch {}
      toast.success('Load batch created');
      setShowAddLoads(false);
      setAddLoadFiles([]);
      resetAddLoad();
      try {
        if (typeof onLoadsUpdated === 'function') onLoadsUpdated(recipe.id, res?.data);
      } catch {}
      reload(true); // Force refresh after creating load
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to create load');
    }
  };

  const submitSession = async (data) => {
    try {
      const res = await firingSessionsAPI.create({
        firearm_id: Number(data.firearm_id),
        load_id: Number(data.load_id),
        location: data.location,
        temperature: data.temperature ? Number(data.temperature) : undefined,
        rounds_fired: Number(data.rounds_fired),
        session_date: data.session_date,
        notes: data.notes || '',
        velocities: (data.velocities || []).map(v => v && v.velocity ? parseFloat(v.velocity) : null).filter(v => v !== null)
      });
      // If user selected files, upload them to the new firing session id and refresh images
      try {
        const newSessionId = res?.data?.id;
        if (newSessionId && Array.isArray(sessionFiles) && sessionFiles.length > 0) {
          await uploadsAPI.upload('firing_session', newSessionId, Array.from(sessionFiles));
          try { window.dispatchEvent(new CustomEvent('uploadsChanged', { detail: { entity: 'firing_session', id: newSessionId, recipeId: recipe.id } })); } catch {}
          // Fetch ALL related images (recipe + all loads + all sessions) to avoid losing existing images
          try {
            const [loadsRes, sessionsRes] = await Promise.all([loadsAPI.getAll(), firingSessionsAPI.getAll()]);
            const loadsList = Array.isArray(loadsRes.data) ? loadsRes.data : [];
            const sessionsList = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
            // Map ALL batch_ids to recipeId (loads are grouped, sessions reference specific IDs)
            const loadIdToRecipeId = new Map();
            loadsList.forEach(l => {
              const rid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
              const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
              ids.forEach(id => loadIdToRecipeId.set(id, rid));
            });
            // Collect all load IDs for the recipe (include all batch_ids)
            const loadIds = [];
            loadsList.forEach(l => {
              if (String(((l && l.recipe && l.recipe.id) || l?.recipe_id)) === String(recipe.id)) {
                const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
                ids.forEach(id => loadIds.push(id));
              }
            });
            const sessionIds = sessionsList.filter(s => {
              const loadId = (s && s.load && s.load.id) || s?.load_id;
              const rid = loadIdToRecipeId.get(loadId);
              return String(rid) === String(recipe.id);
            }).map(s => s.id);
            const calls = [
              uploadsAPI.list('recipe', recipe.id),
              ...loadIds.map(id => uploadsAPI.list('load', id)),
              ...sessionIds.map(id => uploadsAPI.list('firing_session', id)),
            ];
            const respArr = await Promise.all(calls.map(p => p.catch(() => ({ data: [] }))));
            const merged = respArr.flatMap(x => Array.isArray(x?.data) ? x.data : []);
            const uniq = [];
            const seen = new Set();
            merged.forEach(f => { if (f && !seen.has(f.id)) { seen.add(f.id); uniq.push(f); } });
            setImages(uniq);
            try { if (typeof onImagesUpdated === 'function') onImagesUpdated(recipe.id, uniq); } catch {}
          } catch {}
        }
      } catch {}
      toast.success('Firing session recorded');
      window.dispatchEvent(new CustomEvent('firingSessionChanged', {
        detail: {
          action: 'created',
          sessionId: res?.data?.id,
          loadId: data.load_id,
          recipeId: recipe.id
        }
      }));
      setShowSession(false);
      resetSession({ velocities: [] });
      setSessionFiles([]);
      setSessionPreviews([]);
      // Notify parent to refresh loads/stats so the recipe card updates immediately
      try {
        if (typeof onLoadsUpdated === 'function') onLoadsUpdated(recipe.id, res?.data);
      } catch {}
      
      // If velocities were recorded, refresh modal's velocity stats immediately
      const hadVelocities = (data.velocities || []).some(v => v && v.velocity);
      if (hadVelocities) {
        try {
          const vsRes = await recipesAPI.getVelocityStats([recipe.id]);
          const vsData = vsRes?.data || {};
          const key = String(recipe.id);
          setModalVelocityStats(vsData[key] || []);
        } catch {}
      }
      
      reload(true); // Force refresh after creating session
    } catch {
      toast.error('Failed to record firing session');
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    try {
      await uploadsAPI.upload('recipe', recipe.id, Array.from(files));
      // After upload, refetch aggregated set so parent thumbnails reflect load/session ownership too
      try {
        const [loadsRes, sessionsRes] = await Promise.all([loadsAPI.getAll(), firingSessionsAPI.getAll()]);
        const loadsList = Array.isArray(loadsRes.data) ? loadsRes.data : [];
        const sessionsList = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
        // Map ALL batch_ids to recipeId (loads are grouped, sessions reference specific IDs)
        const loadIdToRecipeId = new Map();
        loadsList.forEach(l => {
          const rid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
          const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
          ids.forEach(id => loadIdToRecipeId.set(id, rid));
        });
        // Collect all load IDs for the recipe (include all batch_ids)
        const loadIds = [];
        loadsList.forEach(l => {
          if (String(((l && l.recipe && l.recipe.id) || l?.recipe_id)) === String(recipe.id)) {
            const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
            ids.forEach(id => loadIds.push(id));
          }
        });
        const sessionIds = sessionsList.filter(s => {
          const loadId = (s && s.load && s.load.id) || s?.load_id;
          const rid = loadIdToRecipeId.get(loadId);
          return String(rid) === String(recipe.id);
        }).map(s => s.id);
        const calls = [
          uploadsAPI.list('recipe', recipe.id),
          ...loadIds.map(id => uploadsAPI.list('load', id)),
          ...sessionIds.map(id => uploadsAPI.list('firing_session', id)),
        ];
        const respArr = await Promise.all(calls.map(p => p.catch(() => ({ data: [] }))));
        const merged = respArr.flatMap(x => Array.isArray(x?.data) ? x.data : []);
        const uniq = [];
        const seen = new Set();
        merged.forEach(f => { if (f && !seen.has(f.id)) { seen.add(f.id); uniq.push(f); } });
        setImages(uniq);
        try { if (typeof onImagesUpdated === 'function') onImagesUpdated(recipe.id, uniq); } catch {}
      } catch { /* ignore */ }
      toast.success('Files uploaded');
    } catch (e) {
      toast.error('Failed to upload files');
    }
  };

  const removeImage = async (imageId) => {
    try {
      await uploadsAPI.remove(imageId);
      // After deletion, refetch aggregated to keep parity with parent thumbnails
      try {
        const [loadsRes, sessionsRes] = await Promise.all([loadsAPI.getAll(), firingSessionsAPI.getAll()]);
        const loadsList = Array.isArray(loadsRes.data) ? loadsRes.data : [];
        const sessionsList = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
        // Map ALL batch_ids to recipeId (loads are grouped, sessions reference specific IDs)
        const loadIdToRecipeId = new Map();
        loadsList.forEach(l => {
          const rid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
          const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
          ids.forEach(id => loadIdToRecipeId.set(id, rid));
        });
        // Collect all load IDs for the recipe (include all batch_ids)
        const loadIds = [];
        loadsList.forEach(l => {
          if (String(((l && l.recipe && l.recipe.id) || l?.recipe_id)) === String(recipe.id)) {
            const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
            ids.forEach(id => loadIds.push(id));
          }
        });
        const sessionIds = sessionsList.filter(s => {
          const loadId = (s && s.load && s.load.id) || s?.load_id;
          const rid = loadIdToRecipeId.get(loadId);
          return String(rid) === String(recipe.id);
        }).map(s => s.id);
        const calls = [
          uploadsAPI.list('recipe', recipe.id),
          ...loadIds.map(id => uploadsAPI.list('load', id)),
          ...sessionIds.map(id => uploadsAPI.list('firing_session', id)),
        ];
        const respArr = await Promise.all(calls.map(p => p.catch(() => ({ data: [] }))));
        const merged = respArr.flatMap(x => Array.isArray(x?.data) ? x.data : []);
        const uniq = [];
        const seen = new Set();
        merged.forEach(f => { if (f && !seen.has(f.id)) { seen.add(f.id); uniq.push(f); } });
        setImages(uniq);
        try { if (typeof onImagesUpdated === 'function') onImagesUpdated(recipe.id, uniq); } catch {}
      } catch {
        setImages(prev => (prev || []).filter(i => i.id !== imageId));
      }
      toast.success('File deleted');
    } catch (e) {
      toast.error('Failed to delete file');
    }
  };

  const handlePurchaseCalc = (field, value) => {
    const quantity = parseFloat(field === 'quantity' ? value : purchaseForm.quantity) || 0;
    const total = parseFloat(field === 'total_cost' ? value : purchaseForm.total_cost) || 0;
    const unit = parseFloat(field === 'unit_cost' ? value : purchaseForm.unit_cost) || 0;
    const next = { ...purchaseForm };
    if (field === 'quantity') next.quantity = value;
    if (field === 'total_cost') next.total_cost = value;
    if (field === 'unit_cost') next.unit_cost = value;
    if (field === 'total_cost') {
      const calcUnit = quantity > 0 ? (total / quantity) : 0;
      next.unit_cost = isFinite(calcUnit) ? calcUnit.toFixed(2) : '0.00';
    } else if (field === 'unit_cost' || field === 'quantity') {
      const calcTotal = quantity * unit;
      next.total_cost = isFinite(calcTotal) ? calcTotal.toFixed(2) : '0.00';
    }
    setPurchaseForm(next);
  };

  const submitPurchase = async () => {
    try {
      if (!purchaseComponent) return;
      await purchasesAPI.create({
        component_id: purchaseComponent.id,
        quantity: Number(purchaseForm.quantity || 0),
        total_cost: Number(purchaseForm.total_cost || 0),
        unit_cost: Number(purchaseForm.unit_cost || 0),
        purchase_date: purchaseForm.purchase_date,
        purchase_location: purchaseForm.purchase_location,
        website: purchaseForm.website,
        notes: purchaseForm.notes||''
      });
      toast.success('Purchase recorded');
      setShowPurchaseModal(false);
      // refresh stats/availability
      reload(true); // Force refresh after recording purchase
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to record purchase');
    }
  };

  // Share modal state/handlers
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUsers, setShareUsers] = useState([]);
  const [shareSelected, setShareSelected] = useState({});
  const [shareMessage, setShareMessage] = useState('Check out this recipe!');
  const loadUsers = async () => {
    try {
      setShareLoading(true);
      const res = await usersAPI.listVisible('');
      const myId = JSON.parse(localStorage.getItem('authUser') || '{}')?.id;
      const list = (res.data || []).filter(u => u.id !== myId);
      setShareUsers(list);
    } catch { toast.error('Failed to load users'); }
    finally { setShareLoading(false); }
  };
  useEffect(() => { if (showShare) loadUsers(); }, [showShare]);
  const toggleShareUser = (id) => setShareSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const sendShare = async () => {
    try {
      const ids = Object.keys(shareSelected).filter(id => shareSelected[id]).map(id => Number(id));
      if (ids.length === 0) return;
      if (ids.length === 1) {
        try {
          const up = await conversationsAPI.upsert([ids[0]]);
          const convId = up?.data?.id;
          if (convId) await conversationsAPI.sendMessage(convId, shareMessage, undefined, recipe.id);
        } catch {}
      } else {
        try {
          await conversationsAPI.create(ids, shareMessage, recipe.id);
        } catch {}
      }
      toast.success('Recipe shared');
      setShowShare(false);
    } catch { toast.error('Failed to share recipe'); }
  };

  if (!show || !recipe) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{recipe.is_deleted ? `Deleted — ${recipe.name}` : recipe.name}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl leading-none p-1">×</button>
          </div>
          {!recipe.is_deleted && (
            <div className="mb-4 flex gap-3 flex-wrap">
              <button onClick={openAddLoads} className="btn btn-primary btn-sm">Add Loads</button>
              <button onClick={() => { resetSession({ velocities: [] }); setShowSession(true); }} className="btn btn-secondary btn-sm">Firing Session</button>
              <button onClick={() => setShowShare(true)} className="btn btn-secondary btn-sm">Share Recipe</button>
              {onEdit && (<button onClick={() => onEdit()} className="btn btn-secondary btn-sm">Edit</button>)}
              <label className="btn btn-secondary btn-sm cursor-pointer">
                Upload Files
                <input type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log" multiple className="hidden" onChange={(e) => handleUpload(e.target.files)} />
              </label>
              <button onClick={handlePrintRecipe} className="btn btn-secondary btn-sm" title="Print recipe with QR code">
                <Printer className="h-4 w-4" />
              </button>
              <button
                onClick={async () => {
                  try {
                    toast.loading('Generating PDF...', { id: 'pdf-export' });
                    const res = await recipesAPI.exportPdf(recipe.id);
                    const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${(recipe.name || 'recipe').replace(/[^a-zA-Z0-9]/g, '_')}_export.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    toast.success('PDF exported', { id: 'pdf-export' });
                  } catch (err) {
                    console.error('PDF export failed:', err);
                    toast.error('Failed to export PDF', { id: 'pdf-export' });
                  }
                }}
                className="btn btn-secondary btn-sm"
                title="Export recipe as PDF"
              >
                <FileDown className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Images */}
          <div className="flex flex-wrap gap-3">
            {(images || []).map(file => {
              const src = file.download_url || file.url;
              const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
              return (
                <div key={file.id} className="relative group">
                  {isPdf ? (
                    <PdfThumbnail
                      fileId={file.id}
                      url={src}
                      thumbnailUrl={file.thumbnail_url}
                      filename={file.filename}
                      sizeClass="w-40 h-56"
                      onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                    />
                  ) : (
                    (() => {
                      const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                      const thumb = isImg ? src : (file.thumbnail_url || src);
                      return (
                        <img src={thumb} alt={file.filename} className="w-24 h-24 object-contain bg-white rounded shadow cursor-pointer" onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })} />
                      );
                    })()
                  )}
                  <div className="flex justify-between mt-1 text-xs text-gray-700">
                <button className="underline" onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })}>Open</button>
                    <button className="text-red-600 underline" onClick={() => removeImage(file.id)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Cartridge</label>
              <p className="text-sm text-gray-900">{recipeDetail.cartridge || recipe.cartridge || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Created</label>
              <p className="text-sm text-gray-900">{formatLocalDate(recipe.created_at)}</p>
            </div>
          </div>

          {/* Recipe Statistics */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm font-medium text-gray-700 mb-3">Recipe Statistics</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Total Loads Created:</span>
                <span className="ml-2 font-medium text-gray-900">{getTotalLoadsCreated()}</span>
              </div>
              <div>
                <span className="text-gray-600">Loads Remaining:</span>
                <span className="ml-2 font-medium text-gray-900">{getRemainingLoads()}</span>
              </div>
              <div>
                <span className="text-gray-600">Total Spent:</span>
                <span className="ml-2 font-medium text-gray-900">${getTotalSpent().toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-600">Average Cost per Load:</span>
                <span className="ml-2 font-medium text-gray-900">{(() => { const avg = getAverageCostPerLoad(); return avg == null ? 'NA' : `$${avg.toFixed(2)}`; })()}</span>
              </div>
              <div>
                <span className="text-gray-600">Current Cost per Load:</span>
                <span className="ml-2 font-medium text-gray-900">{recipeStats.currentCostPerRound != null ? `$${Number(recipeStats.currentCostPerRound).toFixed(2)}` : 'NA'}</span>
              </div>
              <div>
                <span className="text-gray-600">Total Spent (computed):</span>
                <span className="ml-2 font-medium text-gray-900">${Number(recipeStats.totalSpent || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Load Batches */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-700">Load Batches</div>
              <div className="text-xs text-gray-500">{getRecipeLoads.length} batch{getRecipeLoads.length === 1 ? '' : 'es'}</div>
            </div>
            <div className="space-y-2">
              {getRecipeLoads.map(l => {
                const caseLabel = (l.case_batch && typeof l.case_batch.firing_count !== 'undefined')
                  ? `${l.case_batch.firing_count}x brass`
                  : 'New brass';
                return (
                  <div key={l.id} className="flex justify-between items-center text-sm bg-white border rounded p-2">
                    <div>
                      <div className="font-medium text-gray-900">{caseLabel}</div>
                      <div className="text-gray-600">
                        {l.date_created ? `Created ${formatLocalDate(l.date_created)}` : 'Creation date unknown'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-700">
                        {Number(l.quantity_created || 0).toLocaleString()} created, {Number(l.remaining_quantity || 0).toLocaleString()} remaining
                      </div>
                      <div className="text-gray-600">
                        {(() => {
                          const tc = Number(l.total_cost);
                          const qty = Number(l.quantity_created);
                          const avg = (!isNaN(tc) && !isNaN(qty) && qty > 0)
                            ? (tc / qty)
                            : (typeof l.computed_cost_per_round === 'number'
                              ? Number(l.computed_cost_per_round)
                              : Number(l.cost_per_round || 0));
                          const showTc = (!isNaN(tc) ? tc : (avg * (qty || 0)));
                          return `$${avg.toFixed(2)}/rd • $${showTc.toFixed(2)} total`;
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
              {getRecipeLoads.length === 0 && (
                <div className="text-sm text-gray-500">No load batches yet for this recipe.</div>
              )}
            </div>
          </div>

          {/* Firing Sessions */}
  {recipeSessions.length > 0 && (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
      <button
        type="button"
        className="w-full flex items-center justify-between text-left"
        onClick={() => setShowSessionList(prev => !prev)}
      >
        <div>
          <p className="text-sm font-medium text-gray-700">Firing Sessions</p>
          <p className="text-xs text-gray-500">{recipeSessions.length} session{recipeSessions.length === 1 ? '' : 's'}</p>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform ${showSessionList ? 'rotate-180' : ''}`}
        />
      </button>
      {showSessionList && (
        <div className="mt-3 space-y-2">
          {recipeSessions.map(session => {
            const vs = session.velocity_statistics || {};
            const hasVelocity = vs.count > 0 || (session.velocity_records && session.velocity_records.length > 0);
            return (
              <button
                key={session.id}
                type="button"
                className="w-full flex flex-col bg-white border rounded px-3 py-2 text-sm hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
                onClick={() => handleSessionDeepLink(session.id)}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="text-left">
                    <div className="font-medium text-gray-900 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                      <span>{formatLocalDate(session.session_date) || 'Unknown date'}</span>
                      <span className="text-xs text-gray-900 font-semibold">
                        {getSessionCaseLabel(session)}
                        {session.load?.case_batch?.name ? ` • ${session.load.case_batch.name}` : ''}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {session.firearm ? `${session.firearm.manufacturer} ${session.firearm.model}` : 'Unknown firearm'}
                      {session.temperature && <span className="ml-2">• {session.temperature}°F</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-800">{Number(session.rounds_fired || 0).toLocaleString()} rounds</div>
                    <div className="text-xs text-blue-600 underline">Edit session</div>
                  </div>
                </div>
                {hasVelocity && (
                  <div className="mt-2 pt-2 border-t border-gray-100 w-full text-left">
                    <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                      {vs.average != null && <span><span className="font-medium">Avg:</span> {Math.round(vs.average)} fps</span>}
                      {vs.standard_deviation != null && <span><span className="font-medium">SD:</span> {Math.round(vs.standard_deviation)}</span>}
                      {vs.extreme_spread != null && <span><span className="font-medium">ES:</span> {Math.round(vs.extreme_spread)}</span>}
                      {vs.min != null && vs.max != null && <span><span className="font-medium">Range:</span> {Math.round(vs.min)}-{Math.round(vs.max)} fps</span>}
                      {vs.count > 0 && <span><span className="font-medium">N:</span> {vs.count}</span>}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  )}

          {/* Description */}
          {recipe.description && (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700">Description</label>
              <p className="text-sm text-gray-900">{recipe.description}</p>
            </div>
          )}

          {/* Velocity by Firearm (if available) */}
          {velocityByFirearm.length > 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-700 mb-2">Velocity by Firearm</div>
              <div className="space-y-1 text-xs">
                {velocityByFirearm.map((r, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-gray-700 truncate pr-2">{r.label}</span>
                    <span className="text-gray-900">Avg {r.average ?? '-'} • ES {r.es ?? '-'} • SD {r.sd ?? '-'} • N {r.count ?? '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Components */}
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">Components</label>
            {/* Component Availability Summary */}
            {(componentAvailability || []).length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-medium text-blue-900 mb-2">Component Availability Summary</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {componentAvailability.map((av, idx) => {
                    const ml = maxLoadsFor(av);
                    const color = ml > 50 ? 'text-green-600' : (ml > 0 ? 'text-yellow-600' : 'text-red-600');
                    // Prefer headstamp label for brass
                    const rcMatch = findRecipeComponentForAvailability(av);
                    const label =
                      rcMatch && rcMatch.component_type && rcMatch.component_type.toLowerCase() === 'brass'
                        ? (rcMatch.component.head_stamp || rcMatch.component.model || rcMatch.component.brand || av.component)
                        : av.component;
                    return (
                      <div key={idx} className="flex justify-between">
                        <span className="text-blue-800">{label}</span>
                        <span className={`font-medium ${color}`}>{ml} loads possible</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {recipe.components?.map((rc, index) => {
                const find = (recipeStats.componentBreakdown || []).find(cb => {
                  try {
                    return (cb.type || '').toLowerCase() === rc.component_type.toLowerCase() && (cb.component || '').toLowerCase().includes(rc.component.brand.toLowerCase()) && (cb.component || '').toLowerCase().includes(rc.component.model.toLowerCase());
                  } catch { return false; }
                });
                const curUnit = (find && typeof find.current_unit_cost === 'number') ? find.current_unit_cost : null;
                const avgUnit = (find && typeof find.average_unit_cost === 'number') ? find.average_unit_cost : null;
                const qty = Number(rc.quantity || 0);
                const curPerLoad = curUnit != null ? (curUnit * qty) : null;
                const avgPerLoad = avgUnit != null ? (avgUnit * qty) : null;
                const availability = (componentAvailability || []).find(av => {
                  try {
                    const compStr = (av.component || '').toLowerCase();
                    return compStr.includes((rc.component.brand || '').toLowerCase()) && compStr.includes((rc.component.model || '').toLowerCase());
                  } catch { return false; }
                });
                const maxLoadsPossible = availability ? Math.floor((Number(availability.available_quantity || 0)) / (qty || 1)) : 0;
                return (
                  <div key={index} className="p-3 bg-gray-50 rounded">
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 text-lg flex items-center justify-between">
                            <span>
                              {rc.component_type.toLowerCase() === 'brass'
                                ? (rc.component.head_stamp || rc.component.model || rc.component.brand)
                                : `${rc.component.brand} ${rc.component.model}`}
                            </span>
                            {!recipe.is_deleted && (
                              <button className="btn btn-secondary btn-xs" title="Purchase" onClick={() => { setPurchaseComponent(rc.component); setPurchaseForm({ quantity:'', total_cost:'', unit_cost:'', purchase_date:new Date().toISOString().split('T')[0], purchase_location:'', website:'', notes:''}); setShowPurchaseModal(true); }}>
                                <ShoppingCart className="h-4 w-4 text-white" />
                              </button>
                            )}
                          </div>
                          <div className={`text-sm uppercase tracking-wide font-medium ${typeColorClass(rc.component_type)}`}>{rc.component_type}</div>
                          <div className="text-sm text-gray-600">Quantity per load: {rc.quantity} {rc.unit}</div>
                          <div className="mt-1 text-xs text-gray-700">
                            <div>Current cost per load: {curPerLoad != null ? `$${curPerLoad.toFixed(2)}` : 'NA'}</div>
                            <div>Average cost per load: {avgPerLoad != null ? `$${avgPerLoad.toFixed(2)}` : 'NA'}</div>
                          </div>
                          {/* Component-specific details */}
                          {rc.component_type.toLowerCase() === 'bullet' && (
                            <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-sm text-gray-600">
                              {rc.component.weight_grains && (
                                <div>Weight: {rc.component.weight_grains} grains</div>
                              )}
                              {rc.component.length_inches && (
                                <div>Length: {rc.component.length_inches}"</div>
                              )}
                              {rc.component.caliber_inches && (
                                <div>Caliber: {rc.component.caliber_inches}"</div>
                              )}
                              {rc.component.core_material && (
                                <div>
                                  Core: {rc.component.core_material}
                                  {rc.component.core_material_other && ` (${rc.component.core_material_other})`}
                                </div>
                              )}
                              {(() => {
                                const selfCan = !!recipe.self_cannelured;
                                const hasCannelure = !!rc.component.cannelured;
                                if (!selfCan && !hasCannelure) return null;
                                const depth = hasCannelure ? rc.component.cannelure_depth : recipe.cannelure_depth;
                                return (
                                  <div>
                                    Cannelure: {selfCan ? 'Self-cannelured' : 'Factory'}{typeof depth === 'number' ? ` • Depth ${depth}"` : ''}
                                  </div>
                                );
                              })()}
                              {rc.component.covering && (
                                <div>
                                  Covering: {rc.component.covering}
                                  {rc.component.covering_other && ` (${rc.component.covering_other})`}
                                </div>
                              )}
                              {rc.component.shape && (
                                <div>
                                  Shape: {rc.component.shape}
                                  {rc.component.shape_other && ` (${rc.component.shape_other})`}
                                </div>
                              )}
                              {rc.component.intended_cartridges && (
                                <div>Intended: {rc.component.intended_cartridges}</div>
                              )}
                            </div>
                          )}
                          {rc.component_type.toLowerCase() === 'primer' && (
                            <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-sm text-gray-600">
                              {rc.component.firearm_type && (
                                <div>Firearm Type: {rc.component.firearm_type}</div>
                              )}
                              {rc.component.primer_size && (
                                <div>Size: {rc.component.primer_size}</div>
                              )}
                              {typeof rc.component.magnum === 'boolean' && (
                                <div>Magnum: {rc.component.magnum ? 'Yes' : 'No'}</div>
                              )}
                            </div>
                          )}
                          {rc.component_type.toLowerCase() === 'brass' && (
                            <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-sm text-gray-600">
                              {rc.component.head_stamp && (
                                <div>Head Stamp: {rc.component.head_stamp}</div>
                              )}
                            </div>
                          )}
                          {availability && (
                            <div className="pt-2 mt-2 border-t border-gray-200">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-600">Available:</span>
                                  <span className="ml-1 font-medium text-gray-900">{Number(availability.available_quantity || 0).toLocaleString()} {availability.unit}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Max loads:</span>
                                  <span className={`ml-1 font-medium ${maxLoadsPossible > 50 ? 'text-green-600' : maxLoadsPossible > 0 ? 'text-yellow-600' : 'text-red-600'}`}>{maxLoadsPossible.toLocaleString()}</span>
                                </div>
                              </div>
                              {rc.component_type.toLowerCase() === 'powder' && availability.unit === 'grains' && (
                                <div className="text-xs text-gray-500 mt-1">({(Number(availability.available_quantity || 0) / 7000).toFixed(2)} lbs available)</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dimensions */}
          {((recipeDetail.case_trim_length ?? recipe.case_trim_length) || (recipeDetail.cartridge_overall_length ?? recipe.cartridge_overall_length) || (recipeDetail.seating_depth ?? recipe.seating_depth)) && (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Dimensions</label>
              <div className="grid grid-cols-3 gap-4">
                {(recipeDetail.case_trim_length ?? recipe.case_trim_length) && (
                  <div>
                    <label className="text-xs text-gray-600">Case Trim Length</label>
                    <p className="text-sm text-gray-900">{(recipeDetail.case_trim_length ?? recipe.case_trim_length)}"</p>
                  </div>
                )}
                {(recipeDetail.cartridge_overall_length ?? recipe.cartridge_overall_length) && (
                  <div>
                    <label className="text-xs text-gray-600">Overall Length</label>
                    <p className="text-sm text-gray-900">{(recipeDetail.cartridge_overall_length ?? recipe.cartridge_overall_length)}"</p>
                  </div>
                )}
                {(recipeDetail.seating_depth ?? recipe.seating_depth) && (
                  <div>
                    <label className="text-xs text-gray-600">Seating Depth</label>
                    <p className="text-sm text-gray-900">{(recipeDetail.seating_depth ?? recipe.seating_depth)}"</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Crimp Settings */}
          {(recipe.crimp_type || recipe.crimp_strength) && (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Crimp Settings</label>
              <div className="grid grid-cols-2 gap-4">
                {recipe.crimp_type && (
                  <div>
                    <label className="text-xs text-gray-600">Crimp Type</label>
                    <p className="text-sm text-gray-900">{recipe.crimp_type}</p>
                  </div>
                )}
                {recipe.crimp_strength && (
                  <div>
                    <label className="text-xs text-gray-600">Crimp Strength</label>
                    <p className="text-sm text-gray-900">{recipe.crimp_strength}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {(recipeDetail.notes ?? recipe.notes) && (
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <p className="text-sm text-gray-900">{recipeDetail.notes ?? recipe.notes}</p>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button onClick={onClose} className="btn btn-secondary">Close</button>
          </div>
        </div>
      </div>

      {/* Add Loads Modal */}
      {showAddLoads && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]" onClick={() => setShowAddLoads(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Loads - {recipe.name}</h3>
              <button onClick={() => setShowAddLoads(false)} className="text-gray-400 hover:text-gray-600 text-3xl leading-none p-1">×</button>
            </div>
            <form onSubmit={handleSubmitAddLoad(submitAddLoads)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label">Recipe</label>
                <input value={recipe.name} readOnly className="input bg-gray-100" />
              </div>
              <div className="md:col-span-2">
                <label className="label">Case Batch (Required)</label>
                <select {...registerAddLoad('case_batch_id', { required: 'Case batch is required' })} className="input" defaultValue="">
                  <option value="">Select case batch...</option>
                  {caseBatches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {batch.component_name} - {batch.firing_count} firings ({batch.remaining_quantity} remaining)
                    </option>
                  ))}
                </select>
                {errorsAddLoad?.case_batch_id && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.case_batch_id.message}</p>)}
              </div>
              <div>
                <label className="label">Quantity to Create</label>
                <input type="number" className="input" placeholder="e.g., 50" {...registerAddLoad('quantity_created', { required: 'Quantity is required', min: { value: 1, message: 'Must be at least 1' } })} />
                {errorsAddLoad?.quantity_created && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.quantity_created.message}</p>)}
              </div>
              <div>
                <label className="label">Date Created</label>
                <input type="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} {...registerAddLoad('date_created', { required: 'Date is required' })} />
                {errorsAddLoad?.date_created && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.date_created.message}</p>)}
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" placeholder="Notes (optional)" {...registerAddLoad('notes')} />
              </div>
              <div className="md:col-span-2">
                <label className="btn btn-secondary btn-sm cursor-pointer">
                  Upload Files
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log"
                    className="hidden"
                    onChange={(e) => setAddLoadFiles(Array.from(e.target.files || []))}
                  />
                </label>
                {Array.isArray(addLoadFiles) && addLoadFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{addLoadFiles.length} file(s) selected. They will upload after creation.</p>
                )}
                {Array.isArray(addLoadFiles) && addLoadFiles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {addLoadFiles.map((f, idx) => {
                      const url = URL.createObjectURL(f);
                      const isPdf = /\.pdf$/i.test(f.name || '');
                      return (
                        <div key={idx} className="relative group">
                          {isPdf ? (
                            <PdfThumbnail
                              fileId={undefined}
                              url={url}
                              filename={f.name}
                              sizeClass="w-16 h-16"
                              onClick={() => setFilePreview({ id: null, url, filename: f.name })}
                            />
                          ) : (
                            <img
                              src={url}
                              alt={f.name}
                              className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer"
                              onClick={() => setFilePreview({ id: null, url, filename: f.name })}
                            />
                          )}
                          <button
                            type="button"
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100"
                            onClick={() => setAddLoadFiles(prev => prev.filter((_, i) => i !== idx))}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn btn-primary">Create Load Batch</button>
                <button type="button" onClick={() => setShowAddLoads(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Session Modal */}
      {showSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]" onClick={() => setShowSession(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">New Firing Session - {recipe.name}</h3>
              <button onClick={() => setShowSession(false)} className="text-gray-400 hover:text-gray-600 text-3xl leading-none p-1">×</button>
            </div>
            <form onSubmit={handleSubmitSession(submitSession)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label">Select Load Batch (with remaining rounds)</label>
                <select className="input" defaultValue="" {...registerSession('load_id', { required: 'Load batch is required' })}>
                  <option value="">Select load batch...</option>
                  {getRecipeLoads.filter(l => (l.remaining_quantity || 0) > 0).map(l => (
                    <option key={l.id} value={l.id}>
                      {l.recipe.name} • {l.remaining_quantity} remaining • {l.case_batch ? `${l.case_batch.firing_count}x brass` : 'new brass'}
                    </option>
                  ))}
                </select>
                {errorsSession?.load_id && (<p className="text-red-500 text-sm mt-1">{errorsSession.load_id.message}</p>)}
              </div>
              <div>
                <label className="label">Firearm</label>
                <select className="input" defaultValue={recipe.firearm?.id || ''} {...registerSession('firearm_id', { required: 'Firearm is required' })}>
                  <option value="">Select firearm...</option>
                  {getCompatibleFirearmsForRecipe(recipe).map(f => (
                    <option key={f.id} value={f.id}>{f.manufacturer} {f.model} ({f.caliber})</option>
                  ))}
                </select>
                {errorsSession?.firearm_id && (<p className="text-red-500 text-sm mt-1">{errorsSession.firearm_id.message}</p>)}
              </div>
              <div>
                <label className="label">Location</label>
                <input type="text" className="input" placeholder="e.g., Local Range" {...registerSession('location', { required: 'Location is required' })} />
                {errorsSession?.location && (<p className="text-red-500 text-sm mt-1">{errorsSession.location.message}</p>)}
              </div>
              <div>
                <label className="label">Date</label>
                <input type="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} {...registerSession('session_date', { required: 'Date is required' })} />
                {errorsSession?.session_date && (<p className="text-red-500 text-sm mt-1">{errorsSession.session_date.message}</p>)}
              </div>
              <div>
                <label className="label">Rounds Fired</label>
                <input
                  type="number"
                  className="input"
                  min={minSessionRounds}
                  max={sessionHasMax ? sessionMaxRounds : undefined}
                  placeholder={sessionRoundsPlaceholder}
                  {...registerSession('rounds_fired', {
                    required: 'Rounds fired is required',
                    validate: (value) => {
                      const num = Number(value);
                      if (!Number.isFinite(num)) return 'Enter a valid number';
                      if (num < minSessionRounds) {
                        return `Must be at least ${minSessionRounds} to cover entered velocities`;
                      }
                      if (sessionHasMax && num > sessionMaxRounds) {
                        return `Cannot exceed ${sessionMaxRounds} remaining rounds`;
                      }
                      return true;
                    },
                  })}
                />
                {errorsSession?.rounds_fired && (<p className="text-red-500 text-sm mt-1">{errorsSession.rounds_fired.message}</p>)}
              </div>
              <div>
                <label className="label">Temperature (°F)</label>
                <input type="number" className="input" placeholder="e.g., 72" {...registerSession('temperature')} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" placeholder="Session notes (optional)" {...registerSession('notes')} />
              </div>
              <div className="md:col-span-2">
                <label className="btn btn-secondary btn-sm cursor-pointer">
                  Upload Files
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log"
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        setSessionFiles((prev) => [...prev, ...files]);
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
                {Array.isArray(sessionFiles) && sessionFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{sessionFiles.length} file(s) selected. They will upload after creation.</p>
                )}
                {Array.isArray(sessionPreviews) && sessionPreviews.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {sessionPreviews.map((p, idx) => {
                      const name = p.filename || '';
                      const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
                      const isPdf = /\.pdf$/i.test(name);
                      const ext = (name.split('.').pop() || '').toUpperCase();
                      return (
                        <div key={idx} className="relative group">
                          {isImage ? (
                            <img
                              src={p.url}
                              alt={name}
                              className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                            />
                          ) : isPdf ? (
                            <PdfThumbnail
                              blob={p.file}
                              url={p.url}
                              filename={name}
                              sizeClass="w-16 h-16"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                            />
                          ) : (
                            <div
                              className="w-16 h-16 bg-gray-100 border rounded shadow flex items-center justify-center cursor-pointer overflow-hidden"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                              title={name}
                            >
                              {p.docThumb ? (
                                <img src={p.url} alt={name} className="w-full h-full object-contain" />
                              ) : (
                                <span className="text-[10px] text-gray-700 font-medium">{ext || 'FILE'}</span>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100"
                            onClick={() => setSessionFiles(prev => prev.filter((_, i) => i !== idx))}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <label className="label">Velocity Records (fps)</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => appendSessionVelocity({ velocity: '' })} className="btn btn-sm btn-primary">
                      <Plus className="w-4 h-4 mr-1" /> Add Velocity
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => sessionImportInputRef.current?.click()}
                      disabled={importingSessionVelocities}
                    >
                      {importingSessionVelocities ? 'Importing…' : 'Import Velocities'}
                    </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={handleClearSessionVelocities}
                    disabled={!hasSessionVelocities}
                  >
                    Clear Velocities
                  </button>
                    <input
                      type="file"
                      ref={sessionImportInputRef}
                      className="hidden"
                    multiple
                      accept=".csv,.xlsx"
                      onChange={handleSessionVelocityFileChange}
                    />
                  </div>
                </div>
                {fieldsSession.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                    {fieldsSession.map((field, index) => (
                      <div key={field.id} className="relative">
                        <input type="number" {...registerSession(`velocities.${index}.velocity`)} className="input text-sm pr-8" placeholder={`Shot ${index + 1}`} />
                        <button type="button" onClick={() => removeSessionVelocity(index)} className="absolute right-1 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700" title="Remove this velocity">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">Click "Add Velocity" to record individual shots or use "Import Velocities" with a CSV/XLSX that has a SPEED (FPS) column.</p>
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn btn-primary">Record Session</button>
                <button type="button" onClick={() => setShowSession(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]" onClick={() => setShowShare(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[80vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share Recipe</h3>
              <button onClick={() => setShowShare(false)} className="text-gray-400 hover:text-gray-600 text-3xl leading-none p-1">×</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label">Message</label>
                <textarea className="input h-24" value={shareMessage} onChange={(e) => setShareMessage(e.target.value)} />
              </div>
              {shareLoading ? (
                <div className="text-sm text-gray-500">Loading users…</div>
              ) : (
                shareUsers.map(u => (
                  <label key={u.id} className="flex items-center justify-between border rounded p-2">
                    <div>
                      <div className="font-medium text-gray-900">{u.display_name || u.email}</div>
                      <div className="text-xs text-gray-600">{u.first_name} {u.last_name}</div>
                    </div>
                    <input type="checkbox" className="h-4 w-4" checked={!!shareSelected[u.id]} onChange={() => toggleShareUser(u.id)} />
                  </label>
                ))
              )}
              <div className="mt-2 flex justify-end gap-2">
                <button className="btn btn-secondary" onClick={() => setShowShare(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={sendShare}>Send</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && purchaseComponent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]" onClick={()=>setShowPurchaseModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Record Purchase — {purchaseComponent.brand} {purchaseComponent.model || purchaseComponent.caliber}</h3>
              <button onClick={()=>setShowPurchaseModal(false)} className="text-gray-400 hover:text-gray-600 text-3xl leading-none p-1">×</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Quantity</label>
                <input type="number" className="input" value={purchaseForm.quantity} onChange={(e)=>handlePurchaseCalc('quantity', e.target.value)} />
              </div>
              <div>
                <label className="label">Purchase Date</label>
                <input type="date" className="input" value={purchaseForm.purchase_date} onChange={(e)=>setPurchaseForm(prev=>({...prev, purchase_date:e.target.value}))} />
              </div>
              <div>
                <label className="label">Total Cost ($)</label>
                <input type="number" step="0.01" className="input" value={purchaseForm.total_cost} onChange={(e)=>handlePurchaseCalc('total_cost', e.target.value)} />
              </div>
              <div>
                <label className="label">Unit Cost ($)</label>
                <input type="number" step="0.01" className="input" value={purchaseForm.unit_cost} onChange={(e)=>handlePurchaseCalc('unit_cost', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Purchase Location</label>
                <input type="text" className="input" value={purchaseForm.purchase_location} onChange={(e)=>setPurchaseForm(prev=>({...prev, purchase_location:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Website</label>
                <input type="url" className="input" value={purchaseForm.website} onChange={(e)=>setPurchaseForm(prev=>({...prev, website:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" value={purchaseForm.notes} onChange={(e)=>setPurchaseForm(prev=>({...prev, notes:e.target.value}))} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={()=>setShowPurchaseModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitPurchase}>Save Purchase</button>
            </div>
          </div>
        </div>
      )}
      <ImagePreviewModal id={filePreview?.id} url={filePreview?.url} filename={filePreview?.filename} thumbnailUrl={filePreview?.thumbnailUrl} onClose={() => setFilePreview(null)} />
    </div>
  );
};

export default RecipeModal;


